<template>
    <div class="c-yn" :style="wrapStyle">
        <div :style="innerStyle">
            <div :style="headerStyle">
                <div :style="eyebrowStyle">{{ agencyName }}</div>
                <h1 :style="h1Style">
                    Did you issue any new SOWs for
                    <span :style="dealAccentStyle">{{ getCurrentDealName }}</span>?
                </h1>
                <p :style="leadStyle">
                    A new SOW is needed when scope changes — let us know if there's one
                    to record for this deal.
                </p>
            </div>

            <CCard :extra-style="cardStyle">
                <div :style="choicePairStyle">
                    <CChoice
                        icon="✓"
                        label="Yes, new SOW"
                        sub="I have it to upload"
                        tone="green"
                        :selected="isHasNewUpsell === true"
                        @click="isHasNewUpsell = true"
                    />
                    <CChoice
                        icon="–"
                        label="No, not this period"
                        sub="No scope changes"
                        tone="neutral"
                        :selected="isHasNewUpsell === false"
                        @click="isHasNewUpsell = false"
                    />
                </div>
            </CCard>

            <div :style="navStyle">
                <CBtn ghost @click="onPrevPage">← Previous</CBtn>
                <CBtn primary big @click="onNextPage">Next →</CBtn>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { SECTIONS } from "../consts";
import { ignorableWatch } from "@vueuse/core";
import { scrollToTop } from "../../../helpers/_scrollToTop";

import CCard from "../c-design/CCard.vue";
import CChoice from "../c-design/CChoice.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import { C_BG, C_INK, C_MUTED, C_HAIR, C_RED, FONT_DISPLAY, navMobileStickyStyle } from "../c-design/tokens";

export default {
    name: "Wizard_Do_You_Have_New_Upsell",
    mixins: [viewportMixin],
    components: { CCard, CChoice, CBtn },
    props: ["onChangeSection"],
    data() {
        return {
            SECTIONS,
            isHasNewUpsell: null,
            isHasNewSow: null,
        };
    },

    mounted() {
        scrollToTop();
    },

    computed: {
        prevSection() {
            return this.$store.getters.getPrevComponent;
        },
        currentBrand() {
            return this.$store.getters.getCurrentBrandName;
        },
        agencyName() {
            return this.$store.getters.getAgencyName;
        },
        isEditUpsell() {
            return this.$store.getters.getIsEditUpsell;
        },
        getCurrentDealId() {
            return (
                this.$store.getters.getSalesPages(
                    this.$store.getters.getSalesPagesIndex
                ).deal.id || null
            );
        },
        getCurrentDealName() {
            return (
                this.$store.getters.getSalesPages(
                    this.$store.getters.getDealSalesIndex
                ).deal_name || null
            );
        },
        sowIndex() {
            return this.$store.getters.getCurrentIndexSOW;
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '780px', margin: '0 auto' };
        },
        headerStyle() {
            return {
                marginBottom: this.isMobile ? '16px' : '24px',
                padding: this.isMobile ? '0 4px' : '0 8px',
            };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px', color: C_MUTED, letterSpacing: '0.6px',
                textTransform: 'uppercase', fontWeight: 600,
            };
        },
        h1Style() {
            return {
                margin: '6px 0 8px', fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '24px' : '30px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.6px' : '-0.8px',
                lineHeight: 1.15, color: C_INK,
            };
        },
        dealAccentStyle() {
            return { color: C_RED };
        },
        leadStyle() {
            return {
                margin: 0,
                fontSize: this.isMobile ? '13.5px' : '15px',
                color: C_MUTED,
                lineHeight: 1.55,
            };
        },
        cardStyle() {
            return {
                marginBottom: this.isMobile ? '14px' : '18px',
                padding: this.isMobile ? '22px' : '32px 36px',
            };
        },
        choicePairStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '10px' : '12px',
            };
        },
        navStyle() {
            return navMobileStickyStyle(this.isMobile, {
                marginTop: '8px', padding: '16px 24px',
                background: '#fff', borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '16px',
            });
        },
    },

    methods: {
        onNextPage() {
            if (
                this.isHasNewUpsell === undefined ||
                this.isHasNewUpsell === null
            ) {
                this.$toast.error("Please select an answer");
                return;
            }

            if (this.isEditUpsell) {
                if (this.isHasNewUpsell) {
                    this.onChangeSection(
                        SECTIONS.SOW,
                        SECTIONS.DO_YOU_HAVE_UPSELL
                    );
                    return;
                } else {
                    this.onChangeSection(
                        SECTIONS.UPSELL,
                        SECTIONS.DO_YOU_HAVE_UPSELL
                    );
                }
                return;
            }

            if (this.isHasNewUpsell) {
                this.onChangeSection(SECTIONS.SOW, SECTIONS.DO_YOU_HAVE_UPSELL);
                return;
            } else {
                this.onChangeSection(
                    SECTIONS.DO_YOU_HAVE_SERVICE_WITHOUT_SOW,
                    SECTIONS.DO_YOU_HAVE_UPSELL
                );
            }
        },
        onPrevPage() {
            if (this.isEditUpsell) {
                this.onChangeSection(SECTIONS.FINAL_DASHBOARD);
            } else {
                this.onChangeSection(
                    SECTIONS.EDIT_SALE_LINE_ITEM,
                    SECTIONS.DASHBOARD
                );
            }
        },
    },
};
</script>
